import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const PatientsContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

const buildRecordServiceKey = (service) => {
  if (!service || typeof service !== "object") return "";
  const tooth = service.tooth ?? service.toothNumber ?? "";
  const name = service.name ?? "";
  const price = Number(
    service.finalPrice ?? service.price ?? service.basePrice ?? 0
  ).toFixed(2);
  return `${tooth}|${name}|${price}`;
};

const dedupeFinancialRecords = (records = []) => {
  if (!Array.isArray(records)) return [];

  const proforma = [];
  const others = [];

  records.forEach((record) => {
    if (record?.pdfData || record?.isProforma) proforma.push(record);
    else others.push(record);
  });

  proforma.sort((a, b) => {
    const dateA = a?.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b?.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA;
  });

  const keyToRecord = new Map();
  const recordKeyMap = new Map();

  proforma.forEach((record) => {
    const services = Array.isArray(record?.services) ? record.services : [];
    const keys = new Set();
    services.forEach((srv) => {
      const key = buildRecordServiceKey(srv);
      if (key) keys.add(key);
    });
    recordKeyMap.set(record, keys);
    keys.forEach((key) => {
      const existing = keyToRecord.get(key);
      if (!existing) {
        keyToRecord.set(key, record);
        return;
      }
      const existingKeys = recordKeyMap.get(existing) || new Set();
      if (keys.size > existingKeys.size) {
        keyToRecord.set(key, record);
      }
    });
  });

  const keepSet = new Set([...keyToRecord.values()]);

  const dedupedProforma = proforma.filter((record) => keepSet.has(record));

  const normalizedOthers = others.slice();
  const combined = [...dedupedProforma, ...normalizedOthers];
  combined.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  return combined;
};

const normalizePatient = (raw = {}) => ({
  id: raw.id || raw._id,
  code: raw.code,
  firstName: raw.firstName || "",
  lastName: raw.lastName || "",
  email: raw.email || "",
  phone: raw.phone || "",
  cnp: raw.cnp || "",
  city: raw.city || "",
  birthDate: raw.birthDate || null,
  isMinor: Boolean(raw.isMinor),
  medicId: raw.medicId || raw.doctorId || "",
  medicEmail: raw.medicEmail || "",
  medicName: raw.medicName || "",
  image: raw.image || "",
  legalRepresentative: {
    firstName: raw.legalRepresentative?.firstName || "",
    lastName: raw.legalRepresentative?.lastName || "",
    city: raw.legalRepresentative?.city || "",
    relation: raw.legalRepresentative?.relation || "",
  },
  notes: raw.notes || "",
  journal: Array.isArray(raw.journal)
    ? raw.journal
        .map((entry) => ({
          type: (() => {
            const explicit = (entry.type || "").toString().trim().toLowerCase();
            if (explicit === "audit" || explicit === "note") return explicit;
            return "note";
          })(),
          id: entry.id || entry._id,
          text: entry.text || "",
          createdBy: entry.createdBy || entry.authorEmail || "",
          createdAt: entry.createdAt || entry.date || null,
          updatedAt: entry.updatedAt || null,
        }))
        .filter((entry) => {
          const text = (entry.text || "").toLowerCase();
          const isLegacyRadiographDelete =
            entry.type !== "audit" &&
            text.includes("radiografie") &&
            /(?:ștear|stear|șters|sters)/u.test(text);
          return !isLegacyRadiographDelete;
        })
    : [],
  financialRecords: dedupeFinancialRecords(raw.financialRecords),
  advanceBalance: Number(raw.advanceBalance ?? 0),
  radiographs: Array.isArray(raw.radiographs)
    ? raw.radiographs.map((rx) => ({
        ...rx,
        addedBy: typeof rx?.addedBy === "string" ? rx.addedBy : "",
      }))
    : [],
  meta: raw.meta || null,
  createdAt: raw.createdAt || null,
  updatedAt: raw.updatedAt || null,
});

export function PatientsProvider({ children }) {
  const { token, currentUser } = useAuth();
  const [pacienti, setPacienti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchPatients = useCallback(async () => {
    if (!token) {
      setPacienti([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/patients`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-au putut încărca pacienții");
      }

      const list = data?.data?.patients || [];
      setPacienti(list.map(normalizePatient));
    } catch (err) {
      console.error("Eroare fetch pacienți:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const createPatient = useCallback(
    async (payload) => {
      if (!token) return null;

      try {
        const res = await fetch(`${API_URL}/api/patients`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut crea pacientul");
        }

        const patient = normalizePatient(data?.data?.patient);
        setPacienti((prev) => [patient, ...prev]);
        return patient;
      } catch (err) {
        console.error("Eroare creare pacient:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const updatePatient = useCallback(
    async (patientId, payload) => {
      if (!patientId || !token) return null;

      try {
        const res = await fetch(`${API_URL}/api/patients/${patientId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut actualiza pacientul");
        }

        const apiPatient = normalizePatient(data?.data?.patient);
        const mergedPatient = (() => {
          if (!payload) return apiPatient;
          const safePayload = Array.isArray(payload)
            ? payload
            : { ...payload };
          const next = { ...safePayload, ...apiPatient };

          if (safePayload.image && !next.image) {
            next.image = safePayload.image;
          }

          if (Array.isArray(safePayload.radiographs) && safePayload.radiographs.length) {
            const hasServerRadiographs =
              Array.isArray(apiPatient.radiographs) && apiPatient.radiographs.length;
            if (!hasServerRadiographs || safePayload.radiographs.length > apiPatient.radiographs.length) {
              next.radiographs = safePayload.radiographs;
            }
          }

          return normalizePatient(next);
        })();

        setPacienti((prev) => prev.map((p) => (p.id === mergedPatient.id ? mergedPatient : p)));
        return mergedPatient;
      } catch (err) {
        console.error("Eroare actualizare pacient:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const deletePatient = useCallback(
    async (patientId) => {
      if (!patientId || !token) return false;

      try {
        const res = await fetch(`${API_URL}/api/patients/${patientId}`, {
          method: "DELETE",
          headers: {
            ...authHeaders,
          },
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut șterge pacientul");
        }

        setPacienti((prev) => prev.filter((p) => p.id !== patientId));
        return true;
      } catch (err) {
        console.error("Eroare ștergere pacient:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const syncPatient = useCallback((payload) => {
    if (!payload) return null;
    const normalized = normalizePatient(payload);
    setPacienti((prev) => {
      const exists = prev.some((p) => p.id === normalized.id);
      if (exists) {
        return prev.map((p) => (p.id === normalized.id ? normalized : p));
      }
      return [normalized, ...prev];
    });
    return normalized;
  }, []);

  const addFinancialRecord = useCallback(
    async (patientId, record) => {
      if (!patientId || !token) return null;

      try {
        const res = await fetch(`${API_URL}/api/patients/${patientId}/financial-records`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(record),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut salva istoricul financiar");
        }

        return syncPatient(data?.data?.patient);
      } catch (err) {
        console.error("Eroare istoric financiar:", err);
        throw err;
      }
    },
    [authHeaders, token, syncPatient]
  );

  const deleteFinancialRecord = useCallback(
    async (patientId, recordId) => {
      if (!patientId || !recordId || !token) return null;

      try {
        const res = await fetch(
          `${API_URL}/api/patients/${patientId}/financial-records/${recordId}`,
          {
            method: "DELETE",
            headers: {
              ...authHeaders,
            },
          }
        );

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut șterge înregistrarea financiară");
        }

        return syncPatient(data?.data?.patient);
      } catch (err) {
        console.error("Eroare ștergere înregistrare financiară:", err);
        throw err;
      }
    },
    [authHeaders, token, syncPatient]
  );

  const addJournalEntry = useCallback(
    async (patientId, text, type = "note") => {
      if (!patientId || !text || !token) return null;

      try {
        const res = await fetch(`${API_URL}/api/patients/${patientId}/journal`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ text, type }),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut salva nota");
        }

        return syncPatient(data?.data?.patient);
      } catch (err) {
        console.error("Eroare adăugare notă jurnal:", err);
        throw err;
      }
    },
    [authHeaders, token, syncPatient]
  );

  const updateJournalEntry = useCallback(
    async (patientId, entryId, text) => {
      if (!patientId || !entryId || !text || !token) return null;

      try {
        const res = await fetch(`${API_URL}/api/patients/${patientId}/journal/${entryId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ text }),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut actualiza nota");
        }

        return syncPatient(data?.data?.patient);
      } catch (err) {
        console.error("Eroare actualizare notă jurnal:", err);
        throw err;
      }
    },
    [authHeaders, token, syncPatient]
  );

  const deleteJournalEntry = useCallback(
    async (patientId, entryId) => {
      if (!patientId || !entryId || !token) return null;

      try {
        const res = await fetch(`${API_URL}/api/patients/${patientId}/journal/${entryId}`, {
          method: "DELETE",
          headers: {
            ...authHeaders,
          },
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut șterge nota");
        }

        return syncPatient(data?.data?.patient);
      } catch (err) {
        console.error("Eroare ștergere notă jurnal:", err);
        throw err;
      }
    },
    [authHeaders, token, syncPatient]
  );

  const contextValue = useMemo(
    () => ({
      pacienti,
      loading,
      error,
      currentUser,
      refreshPatients: fetchPatients,
      createPatient,
      updatePatient,
      deletePatient,
      addFinancialRecord,
      deleteFinancialRecord,
      addJournalEntry,
      updateJournalEntry,
      deleteJournalEntry,
      syncPatient,
    }),
    [
      pacienti,
      loading,
      error,
      currentUser,
      fetchPatients,
      createPatient,
      updatePatient,
      deletePatient,
      addFinancialRecord,
      deleteFinancialRecord,
      addJournalEntry,
      updateJournalEntry,
      deleteJournalEntry,
      syncPatient,
    ]
  );

  return (
    <PatientsContext.Provider value={contextValue}>
      {children}
    </PatientsContext.Provider>
  );
}

export const usePatients = () => useContext(PatientsContext);
